import { ErrorObject } from 'ajv';
import { FormikValues } from 'formik';
import { RE_ENTER_PROPERTY_ID_PREFIX } from 'reEnter';
import { FormPage, FormPageProperties, PROPERTY_TYPE } from 'shared';
import { ajv } from '../ajv';
import { convertFormPageInputDataForValidation } from '../convertFormPageInputDataForValidation';

// FormikErrors
export type ValidationErrors = {
  [propertyId: string]: string[];
};
const isStringTruthy = (str: string | undefined): boolean =>
  str != null && str !== 'string';

const convertIntoString = (text?: string) =>
  (typeof text === 'string' && text) || '';

const getErrorsFormAjvResult = (ajvErrors?: ErrorObject[] | null) => {
  if (!ajvErrors) {
    return {};
  }
  return ajvErrors.reduce((acc, error) => {
    const _propertyId = error.instancePath.split('/')[1];
    const propertyId =
      _propertyId ??
      error.params.errors?.[0].params.missingProperty ??
      error.params.missingProperty;
    const propertyCurrentErrors = acc[propertyId] || [];
    return {
      ...acc,
      [propertyId]: [
        ...propertyCurrentErrors,
        convertIntoString(error.message),
      ],
    };
  }, {} as ValidationErrors);
};

const getReEnterValueErrors = (
  formPage: FormPage,
  values: FormikValues,
): ValidationErrors => {
  const { properties, required } = formPage;
  if (!properties) {
    return {};
  }
  const reEnterEntries = Object.entries(values).filter(([propertyId]) =>
    propertyId.startsWith(RE_ENTER_PROPERTY_ID_PREFIX),
  );
  return reEnterEntries.reduce((acc, [propertyId, value]) => {
    const originKey = propertyId.replace(RE_ENTER_PROPERTY_ID_PREFIX, '');
    const originValue = values[originKey];
    const originProperty = properties[originKey];
    if (
      originProperty.propertyType !== PROPERTY_TYPE.mailAddress &&
      originProperty.propertyType !== PROPERTY_TYPE.text &&
      originProperty.propertyType !== PROPERTY_TYPE.passwordHash
    ) {
      return acc;
    }
    const reEnterErrorMessage = originProperty.reEnter?.errorMessage;
    const isRequired = required?.includes(originKey) ?? false;
    const errorMessage = convertIntoString(reEnterErrorMessage);
    if (isStringTruthy(originValue)) {
      return originValue === value
        ? acc
        : {
            ...acc,
            [propertyId]: [errorMessage],
          };
    }
    if (isStringTruthy(value)) {
      return {
        ...acc,
        [propertyId]: [errorMessage],
      };
    }

    // (value === '' || value == null)
    //    && (originValue === '' || originValue == null)
    if (isRequired) {
      return {
        ...acc,
        [propertyId]: [errorMessage],
      };
    }
    return acc;
  }, {} as ValidationErrors);
};

export const validator =
  (formPage: FormPage) =>
  (values: FormikValues): ValidationErrors => {
    const valuesForValidation = convertFormPageInputDataForValidation(
      formPage.properties as FormPageProperties,
      values,
    );
    ajv.validate(formPage, valuesForValidation);
    const errorsFromAjv = getErrorsFormAjvResult(ajv.errors);
    const errorsFromReEnter = getReEnterValueErrors(
      formPage,
      valuesForValidation,
    );
    return {
      ...errorsFromAjv,
      ...errorsFromReEnter,
    };
  };
